import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { QuoteCTA } from "@/components/QuoteCTA";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  ArrowRight,
  FileText,
  Ruler,
  HardHat,
  ShieldCheck,
  Wrench,
  Mail,
  Phone,
} from "lucide-react";
import hero from "@/assets/awning-frame-fabric-patterning-shop.jpg";

export const Route = createFileRoute("/architects")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "For Architects & Specifiers — Shade Structure Spec Sheets, CAD & Warranty | Texas Made Shade",
      description:
        "Architect & specifier resources for Texas Made Shade — fabric spec sheets, hardware schedules, CAD/DWG samples.",
      image: hero,
      imageAlt:
        "Texas Made Shade fabricator patterning a welded aluminum awning frame in the Seguin shop — engineered fabrication for architects and specifiers",
      socialTitle: "Architect & Specifier Hub — Texas Made Shade",
      socialDescription:
        "Spec sheets, CAD samples, hardware schedules and warranty docs for shade structures, awnings and canopies.",
      keywords:
        "architect shade structure specifier, awning spec sheet Texas, sail shade CAD DWG, shade structure hardware schedule, Sunbrella spec sheet, Serge Ferrari spec, awning warranty PDF, Texas commercial shade specifier",
    }),
    scripts: serviceHeadScripts({
      path: "/architects",
      serviceName: "Architect & Specifier Resources",
      breadcrumbName: "For Architects",
      description:
        "Architect & specifier hub for Texas Made Shade — spec sheets, CAD samples, hardware schedules, engineering letters and warranty documentation for commercial.",
      faqs: [
        {
          q: "Do you provide CAD/DWG samples for shade structure specifications?",
          a: "Yes — we share representative DWG and PDF samples for our welded aluminum awning frames, sail-shade post assemblies and tension hardware schedules so architects can spec our build without guesswork. Email specs@texasmadeshade.com with your project type.",
        },
        {
          q: "Can you provide engineering letters for permitted commercial shade work in Texas?",
          a: "Yes — we work with licensed Texas PEs to deliver stamped engineering letters for permitted commercial sail-shade, awning and covered-parking projects. Send your wind/uplift load requirements with the project request.",
        },
        {
          q: "What is the warranty on your awnings and shade structures?",
          a: "Welded aluminum frames carry a 10-year structural warranty. Sunbrella® and Tempotest® awning canvas carries the manufacturer's 10-year limited warranty. Serge Ferrari® and Phifertex® shade fabrics carry their respective manufacturer warranties (typically 10-year UV).",
        },
        {
          q: "Do you submit AIA-style spec packages?",
          a: "Yes — for commercial projects we deliver a full submittal package: fabric data sheets, hardware schedule, frame spec, finish schedule, engineering letter and warranty documentation, formatted for the architect or GC's submittal process.",
        },
      ],
    }),
  }),
  component: ArchitectsPage,
});

interface ResourceCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  bullets: string[];
}

const RESOURCES: ResourceCard[] = [
  {
    icon: <FileText className="h-6 w-6" aria-hidden="true" />,
    title: "Fabric Spec Sheets",
    description:
      "Manufacturer data sheets for every awning canvas and shade fabric we install. UV ratings, tensile strength, fire-rating and warranty terms.",
    bullets: [
      "Sunbrella® Marine, Furniture & Awning grade",
      "Tempotest® Heritage and Star marine canvas",
      "Serge Ferrari® Soltis & precontraint shade fabric",
      "Phifertex® coated polyester mesh",
    ],
  },
  {
    icon: <Ruler className="h-6 w-6" aria-hidden="true" />,
    title: "CAD & DWG Samples",
    description:
      "Representative DWG / PDF samples for welded aluminum awning frames, sail-shade post assemblies and pavilion enclosures.",
    bullets: [
      "Storefront entry & dome awning frames",
      "Sail-shade post + tension hardware details",
      "Cantilever covered-parking post sections",
      "Pavilion drop-curtain enclosure layouts",
    ],
  },
  {
    icon: <Wrench className="h-6 w-6" aria-hidden="true" />,
    title: "Hardware Schedule",
    description:
      "Standard hardware schedule used on Texas Made Shade commercial structures — stainless tension hardware, welded brackets, anchor specs.",
    bullets: [
      "Stainless tension fittings & turnbuckles",
      "Welded steel posts — engineered to local wind load",
      "Engineered concrete footing details",
      "Powder-coated finish schedule (RAL match available)",
    ],
  },
  {
    icon: <HardHat className="h-6 w-6" aria-hidden="true" />,
    title: "Engineering Letters",
    description:
      "Stamped engineering for permitted commercial sail-shade, awning and covered-parking work — coordinated with licensed Texas PEs.",
    bullets: [
      "Site-specific wind / uplift calcs",
      "Stamped TX PE letters on request",
      "Coordination with GC and AHJ",
      "Engineered footings and post sections",
    ],
  },
  {
    icon: <ShieldCheck className="h-6 w-6" aria-hidden="true" />,
    title: "Warranty Documentation",
    description:
      "Frame, fabric and hardware warranty docs provided with your project documentation on request.",
    bullets: [
      "10-year structural frame warranty",
      "10-year Sunbrella® / Tempotest® canvas",
      "10-year Serge Ferrari® / Phifertex® UV",
      "Workmanship warranty on installation",
    ],
  },
  {
    icon: <FileText className="h-6 w-6" aria-hidden="true" />,
    title: "Full Submittal Packages",
    description:
      "AIA-style submittal packages assembled per project — formatted for the architect or general contractor's submittal process.",
    bullets: [
      "Fabric & hardware data sheets",
      "Frame & finish schedule",
      "Stamped engineering letter",
      "Warranty + maintenance documentation",
    ],
  },
];

const PILLAR_LINKS: { to: string; label: string; blurb: string }[] = [
  { to: "/commercial-awnings", label: "Commercial Awnings", blurb: "Storefront, restaurant, hotel & office entry awnings." },
  { to: "/restaurant-awnings", label: "Restaurant Awnings", blurb: "Branded patio canopies & walk-up window awnings." },
  { to: "/school-shade-structures", label: "School Shade Structures", blurb: "Playground, cafeteria & athletic shade for ISDs." },
  { to: "/church-shade-structures", label: "Church Shade Structures", blurb: "Courtyard, entry & pavilion shade for churches." },
  { to: "/car-wash-shade-sails", label: "Car Wash Shade Sails", blurb: "Vacuum bay & detail bay engineered shade sails." },
  { to: "/covered-parking", label: "Covered Parking Structures", blurb: "Cantilever & sail covered-parking shade." },
  { to: "/playground-shade", label: "Playground Shade Sails", blurb: "Engineered playground sails for schools & parks." },
  { to: "/sail-shades", label: "Sail Shades", blurb: "Engineered tensioned sail-shade structures." },
  { to: "/pergola-covers", label: "Patio Covers", blurb: "Welded aluminum residential & commercial patio covers." },
  { to: "/custom-fabrication", label: "Custom Fabrication", blurb: "Welded steel & aluminum structural fabrication." },
];

function ArchitectsPage() {
  return (
    <div className="min-h-screen bg-cream text-navy-deep">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <img
          src={hero}
          alt="Texas Made Shade fabricator patterning a welded aluminum awning frame in the Seguin shop"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
          loading="eager"
        />
        <div className="relative mx-auto max-w-7xl px-4 py-20 lg:px-8 lg:py-28">
          <Breadcrumbs items={[{ label: "For Architects" }]} className="text-white/80" />
          <div className="mt-6 font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ ARCHITECT &amp; SPECIFIER HUB ★
          </div>
          <h1 className="mt-3 max-w-4xl font-display text-4xl font-bold uppercase leading-tight md:text-6xl">
            Spec Sheets, CAD &amp; Warranty Docs for Architects &amp; Specifiers
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/85 md:text-xl">
            Texas Made Shade is a full-service fabrication shop in Seguin, TX —
            we design, fabricate and install welded aluminum awnings, tensioned
            sail-shade structures.
          </p>
          <p className="mt-4 max-w-3xl text-lg text-white/85 md:text-xl">
            Need <strong>engineered, stamped plans</strong> for permitting or a
            commercial submittal? We can have those produced for you through
            the licensed Texas engineers we work with — just let us know with
            your spec request and we'll fold the engineering scope into your
            quote.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="mailto:specs@texasmadeshade.com?subject=Architect%20%2F%20Specifier%20Submittal%20Request"
              className="inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white shadow-lg ring-1 ring-red-deep/40 transition hover:bg-red-deep"
            >
              <Mail className="h-4 w-4" aria-hidden="true" /> Request Submittal Package
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href="tel:+18303909588"
              className="inline-flex items-center gap-2 border-2 border-white/70 bg-white/10 px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> +1 (830) 390-9588
            </a>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="bg-cream py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ SPECIFIER RESOURCES ★
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase leading-tight md:text-4xl">
            Documents We Can Provide for Your Commercial Project
          </h2>
          <p className="mt-3 max-w-3xl text-base text-navy-deep/75">
            Tell your reviewer or engineer what they need and we'll get it to
            you — spec sheets, sample drawings, mockups and warranty docs.
            These are available options, pulled together per project, so you're
            not chasing loose paperwork.
          </p>
          <ul className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {RESOURCES.map((r) => (
              <li
                key={r.title}
                className="flex h-full flex-col border-2 border-navy-deep/15 bg-white p-6 transition hover:border-red"
              >
                <div className="flex items-center gap-3 text-red">
                  {r.icon}
                  <h3 className="font-display text-lg font-bold uppercase tracking-wide text-navy-deep">
                    {r.title}
                  </h3>
                </div>
                <p className="mt-3 text-sm text-navy-deep/80">{r.description}</p>
                <ul className="mt-4 space-y-1.5 text-sm text-navy-deep/80">
                  {r.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pillar service links */}
      <section className="border-t-8 border-navy-deep bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ PILLAR SERVICES ★
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase leading-tight md:text-4xl">
            Specify Texas Made Shade by Application
          </h2>
          <p className="mt-3 max-w-3xl text-base text-navy-deep/75">
            Each pillar service page below includes representative photos,
            fabric and frame specs, FAQs and our standard warranty terms for
            that application.
          </p>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLAR_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="group flex h-full flex-col border-l-4 border-red bg-cream/60 p-5 transition hover:bg-cream"
                >
                  <span className="font-display text-base font-bold uppercase tracking-wide text-navy-deep group-hover:text-red">
                    {l.label}
                  </span>
                  <span className="mt-1 text-sm text-navy-deep/75">{l.blurb}</span>
                  <span className="mt-3 inline-flex items-center gap-1 font-display text-[11px] uppercase tracking-[0.25em] text-red">
                    View spec page <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Submittal request CTA */}
      <section className="bg-navy-deep py-20 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ REQUEST A SUBMITTAL PACKAGE ★
          </div>
          <h2 className="mt-3 font-display text-3xl font-bold uppercase leading-tight md:text-4xl">
            Send Us Your Project — We'll Send a Full Spec Package
          </h2>
          <p className="mt-4 text-base text-white/80 md:text-lg">
            Email <a href="mailto:specs@texasmadeshade.com" className="underline decoration-red underline-offset-4 hover:text-red">specs@texasmadeshade.com</a> with
            project type, location, AHJ and what your reviewer needs. We'll
            pull together the documentation for your project — fabric data
            sheets, sample or mockup drawings, hardware specs and warranty
            docs — and coordinate stamped engineering letters with the
            licensed Texas PE on the job.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="mailto:specs@texasmadeshade.com?subject=Architect%20%2F%20Specifier%20Submittal%20Request"
              className="inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white shadow-lg ring-1 ring-red-deep/40 transition hover:bg-red-deep"
            >
              <Mail className="h-4 w-4" aria-hidden="true" /> Email Specs Team
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href="tel:+18303909588"
              className="inline-flex items-center gap-2 border-2 border-white/70 px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> +1 (830) 390-9588
            </a>
          </div>
        </div>
      </section>

      <QuoteCTA
        title="Ready to Spec Texas Made Shade?"
        subtitle="Send the project type, location, AHJ and any wind/uplift requirements — we'll respond same business day with a written submittal."
      />

      <SiteFooter />
    </div>
  );
}
